import React, { useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, X, Users } from "lucide-react";
import { createTask } from "../data/requests/task-requests";
import { getUsersForTasks } from "../data/requests/user-requests";
import { toast } from "@/components/ui/use-toast";

export function CreateTaskButton({ className, pageUrl: customPageUrl, iconOnly = false }) {
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    priority: "Normal",
    assignedToIds: [],
  });

  const queryClient = useQueryClient();

  const { data: users = [] } = useQuery({
    queryKey: ["users-for-tasks"],
    queryFn: () => getUsersForTasks().then((res) => res.data),
  });

  const createTaskMutation = useMutation({
    mutationFn: (data) => createTask(data),
    onSuccess: () => {
      queryClient.invalidateQueries(["tasks"]);
      queryClient.invalidateQueries(["my-task-count"]);
      toast({
        title: "Başarılı",
        description: "Görev oluşturuldu",
      });
      setOpen(false);
      resetForm();
    },
    onError: () => {
      toast({
        title: "Hata",
        description: "Görev oluşturulurken hata oluştu",
        variant: "destructive",
      });
    },
  });

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      priority: "Normal",
      assignedToIds: [],
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast({
        title: "Hata",
        description: "Başlık boş olamaz",
        variant: "destructive",
      });
      return;
    }

    if (formData.assignedToIds.length === 0) {
      toast({
        title: "Hata",
        description: "En az bir kullanıcı seçmelisiniz",
        variant: "destructive",
      });
      return;
    }

    // Eğer customPageUrl prop'u verilmişse onu kullan, yoksa mevcut sayfayı kullan
    const pageUrl = customPageUrl || window.location.href;

    createTaskMutation.mutate({
      ...formData,
      pageUrl,
    });
  };

  const handleUserToggle = (userId) => {
    const userIdStr = userId.toString();
    setFormData((prev) => {
      const isSelected = prev.assignedToIds.includes(userIdStr);
      return {
        ...prev,
        assignedToIds: isSelected
          ? prev.assignedToIds.filter((id) => id !== userIdStr)
          : [...prev.assignedToIds, userIdStr],
      };
    });
  };

  const removeUser = (userId) => {
    setFormData((prev) => ({
      ...prev,
      assignedToIds: prev.assignedToIds.filter((id) => id !== userId),
    }));
  };

  const getSelectedUsers = () => {
    return users.filter((user) =>
      formData.assignedToIds.includes(user.id.toString())
    );
  };

  const isUserSelected = (userId) => {
    return formData.assignedToIds.includes(userId.toString());
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className={className} variant="outline" size={iconOnly ? "icon" : "default"}>
          <CheckCircle2 className={iconOnly ? "h-4 w-4" : "h-4 w-4 mr-2"} />
          {!iconOnly && "Görev Oluştur"}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[525px]">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Yeni Görev Oluştur</DialogTitle>
            <DialogDescription>
              Bu sayfa için yeni bir görev oluşturun ve kullanıcılara atayın.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="title">Başlık *</Label>
              <Input
                id="title"
                placeholder="Görev başlığını girin"
                value={formData.title}
                onChange={(e) =>
                  setFormData({ ...formData, title: e.target.value })
                }
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="description">Açıklama</Label>
              <Textarea
                id="description"
                placeholder="Görev açıklamasını girin (opsiyonel)"
                value={formData.description}
                onChange={(e) =>
                  setFormData({ ...formData, description: e.target.value })
                }
                rows={3}
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="priority">Öncelik</Label>
              <Select
                value={formData.priority}
                onValueChange={(value) =>
                  setFormData({ ...formData, priority: value })
                }
              >
                <SelectTrigger id="priority">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Düşük">Düşük</SelectItem>
                  <SelectItem value="Normal">Normal</SelectItem>
                  <SelectItem value="Acil">Acil</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="grid gap-2">
              <Label className="flex items-center gap-2">
                <Users className="h-4 w-4" />
                Atanan Kullanıcılar * ({formData.assignedToIds.length} seçili)
              </Label>

              {/* Seçili kullanıcılar */}
              {formData.assignedToIds.length > 0 && (
                <div className="flex flex-wrap gap-2 p-2 bg-muted rounded-md">
                  {getSelectedUsers().map((user) => (
                    <Badge
                      key={user.id}
                      variant="secondary"
                      className="flex items-center gap-1"
                    >
                      {user.name}
                      <X
                        className="h-3 w-3 cursor-pointer hover:text-destructive"
                        onClick={() => removeUser(user.id.toString())}
                      />
                    </Badge>
                  ))}
                </div>
              )}

              {/* Kullanıcı listesi */}
              <div className="border rounded-md max-h-[200px] overflow-y-auto">
                {users.map((user) => (
                  <label
                    key={user.id}
                    className="flex items-center space-x-3 p-3 hover:bg-muted cursor-pointer border-b last:border-b-0"
                  >
                    <Checkbox
                      checked={isUserSelected(user.id)}
                      onCheckedChange={() => handleUserToggle(user.id)}
                    />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{user.name}</p>
                      <p className="text-xs text-muted-foreground truncate">
                        {user.email}
                      </p>
                    </div>
                  </label>
                ))}
                {users.length === 0 && (
                  <div className="p-4 text-center text-muted-foreground text-sm">
                    Kullanıcı bulunamadı
                  </div>
                )}
              </div>
            </div>

            <div className="bg-muted p-3 rounded-md">
              <p className="text-sm text-muted-foreground">
                <strong>İlgili Sayfa:</strong> {customPageUrl || window.location.href}
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setOpen(false)}
            >
              İptal
            </Button>
            <Button type="submit" disabled={createTaskMutation.isPending}>
              {createTaskMutation.isPending ? "Oluşturuluyor..." : "Oluştur"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
